import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Fingerprint,
  CalendarCheck,
  MessageSquare,
  AlertTriangle,
  FileText,
  GitBranch,
  Calculator,
  ArrowRight,
  ArrowLeft,
  Mail,
  Globe,
  CheckCircle2,
  Clock,
  MapPin,
  Building2,
  KeyRound,
  Lock,
  Smartphone,
  Monitor,
  Menu,
  X,
  Eye,
  Mic,
  ClipboardList,
  TrendingUp,
  Layers,
  Repeat,
  FileSpreadsheet,
  UserCog,
  Timer,
  Banknote,
  Workflow,
  Phone,
  User,
  BarChart3,
  Shield,
  EyeOff,
  Zap,
  MonitorSmartphone,
} from "lucide-react";
import { sessionManager } from "../../utils/sessionManager";
import { LanguageContext } from "../../constant/LanguageContext";
import DemoRequestModal from "./DemoRequestModal";

import { translations } from "./translations";
import "./LandingPage.css";
import logoDark from "../../Images/English Nexploy V2.png";
import logoWhite from "../../Images/English Nexploy white V2.png";
import nexployIcon from "../../Images/Icon_Nexploy.ico";

/* Product screenshots — real Nexploy UI with fictional demo data.
   Each screen exists in English and Arabic (RTL); SHOTS picks the set
   matching the landing page language. */
import shotDashboardEn from "./assets/nexploy-dashboard-demo.webp";
import shotReportsEn from "./assets/nexploy-reports-demo.webp";
import shotAttendanceEn from "./assets/nexploy-attendance-demo.webp";
import shotPayrollEn from "./assets/nexploy-payroll-demo.webp";
import shotLeaveEn from "./assets/nexploy-leaves-demo.webp";
import shotMessengerEn from "./assets/nexploy-messenger-demo.webp";
import shotProfileEn from "./assets/nexploy-employees-demo.webp";
import shotEscalationsEn from "./assets/nexploy-escalations-demo.webp";
import shotDashboardAr from "./assets/nexploy-dashboard-demo-ar.webp";
import shotReportsAr from "./assets/nexploy-reports-demo-ar.webp";
import shotAttendanceAr from "./assets/nexploy-attendance-demo-ar.webp";
import shotPayrollAr from "./assets/nexploy-payroll-demo-ar.webp";
import shotLeaveAr from "./assets/nexploy-leaves-demo-ar.webp";
import shotMessengerAr from "./assets/nexploy-messenger-demo-ar.webp";
import shotProfileAr from "./assets/nexploy-employees-demo-ar.webp";
import shotEscalationsAr from "./assets/nexploy-escalations-demo-ar.webp";

import mobDashboardEn from "./assets/nexploy-mobile-dashboard-demo.webp";
import mobCheckinEn from "./assets/nexploy-mobile-checkin-demo.webp";
import mobSalaryEn from "./assets/nexploy-mobile-salary-demo.webp";
import mobLeavesEn from "./assets/nexploy-mobile-leaves-demo.webp";
import mobMessagesEn from "./assets/nexploy-mobile-messages-demo.webp";
import mobRequestsEn from "./assets/nexploy-mobile-requests-demo.webp";
import mobPayrollEn from "./assets/nexploy-mobile-payroll-demo.webp";
import mobDashboardAr from "./assets/nexploy-mobile-dashboard-demo-ar.webp";
import mobCheckinAr from "./assets/nexploy-mobile-checkin-demo-ar.webp";
import mobSalaryAr from "./assets/nexploy-mobile-salary-demo-ar.webp";
import mobLeavesAr from "./assets/nexploy-mobile-leaves-demo-ar.webp";
import mobMessagesAr from "./assets/nexploy-mobile-messages-demo-ar.webp";
import mobRequestsAr from "./assets/nexploy-mobile-requests-demo-ar.webp";
import mobPayrollAr from "./assets/nexploy-mobile-payroll-demo-ar.webp";
import viewUserEn from "./assets/nexploy-view-user-demo.webp";
import viewManagerEn from "./assets/nexploy-view-manager-demo.webp";
import viewAdminEn from "./assets/nexploy-view-admin-demo.webp";
import adminLevelsEn from "./assets/nexploy-admin-levels-demo.webp";
import viewUserAr from "./assets/nexploy-view-user-demo-ar.webp";
import viewManagerAr from "./assets/nexploy-view-manager-demo-ar.webp";
import viewAdminAr from "./assets/nexploy-view-admin-demo-ar.webp";
import adminLevelsAr from "./assets/nexploy-admin-levels-demo-ar.webp";

const SHOTS = {
  en: {
    shotDashboard: shotDashboardEn,
    shotReports: shotReportsEn,
    shotAttendance: shotAttendanceEn,
    shotPayroll: shotPayrollEn,
    shotLeave: shotLeaveEn,
    shotMessenger: shotMessengerEn,
    shotProfile: shotProfileEn,
    shotEscalations: shotEscalationsEn,
    mobDashboard: mobDashboardEn,
    mobCheckin: mobCheckinEn,
    mobSalary: mobSalaryEn,
    mobLeaves: mobLeavesEn,
    mobMessages: mobMessagesEn,
    mobRequests: mobRequestsEn,
    mobPayroll: mobPayrollEn,
    viewUser: viewUserEn,
    viewManager: viewManagerEn,
    viewAdmin: viewAdminEn,
    adminLevels: adminLevelsEn,
  },
  ar: {
    shotDashboard: shotDashboardAr,
    shotReports: shotReportsAr,
    shotAttendance: shotAttendanceAr,
    shotPayroll: shotPayrollAr,
    shotLeave: shotLeaveAr,
    shotMessenger: shotMessengerAr,
    shotProfile: shotProfileAr,
    shotEscalations: shotEscalationsAr,
    mobDashboard: mobDashboardAr,
    mobCheckin: mobCheckinAr,
    mobSalary: mobSalaryAr,
    mobLeaves: mobLeavesAr,
    mobMessages: mobMessagesAr,
    mobRequests: mobRequestsAr,
    mobPayroll: mobPayrollAr,
    viewUser: viewUserAr,
    viewManager: viewManagerAr,
    viewAdmin: viewAdminAr,
    adminLevels: adminLevelsAr,
  },
};


/* The contact number the user asked for. Kept as one constant so the
   displayed digits (locale-formatted) and the dialled/messaged value
   never drift. */
const CONTACT_PHONE_TEL = "tel:+201010706163";
const CONTACT_WHATSAPP_URL = "https://wa.me/201010706163";

/* ------------------------------------------------------------------ */
/*  Language-independent content — icons and images never change.      */
/*  Text is pulled from ./translations.js by array position.           */
/* ------------------------------------------------------------------ */

const NAV_KEYS = ["platform", "attendance", "payroll", "mobile", "control"];
const NAV_HREFS = [
  "#platform",
  "#attendance",
  "#payroll",
  "#mobile",
  "#control",
];

const TRUST_ICONS = [CheckCircle2, Monitor, Smartphone, Repeat];

/* How long each product-tour screen stays up before autoplay advances. */
const TOUR_MS = 6500;

const TAB_META = [
  { id: "manage", img: "shotProfile", icon: UserCog },
  { id: "track", img: "shotAttendance", icon: Fingerprint },
  { id: "request", img: "shotLeave", icon: CalendarCheck },
  { id: "communicate", img: "shotMessenger", icon: MessageSquare },
  { id: "analyze", img: "shotReports", icon: TrendingUp },
];

const SELF_SERVICE_ICONS = [
  Eye,
  Clock,
  Banknote,
  CalendarCheck,
  FileText,
  TrendingUp,
  AlertTriangle,
  MessageSquare,
];
/* Only screens that aren't already shown in the hero / attendance / payroll
   blocks — repeating them here taught the visitor nothing new. */
const MOBILE_SCREEN_IMGS = ["mobLeaves", "mobMessages", "mobRequests", "mobPayroll"];
const WORKFLOW_ICONS = [ClipboardList, Workflow, GitBranch, CheckCircle2];
/* The three views of the in-app view switcher (User / Manager / Admin). */
const VIEW_META = [
  { id: "user", icon: User, img: "viewUser" },
  { id: "manager", icon: BarChart3, img: "viewManager" },
  { id: "admin", icon: Shield, img: "viewAdmin" },
];

/* Admin-level steps and the three page states, mirroring the permission editor. */
const LEVEL_STEP_ICONS = [UserCog, Layers, KeyRound];
const PAGE_STATE_META = [
  { id: "hidden", icon: EyeOff },
  { id: "locked", icon: Lock },
  { id: "full", icon: Eye },
];
const CONTROL_EXTRA_ICONS = [Zap, MonitorSmartphone, Building2, CheckCircle2, Lock];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function dashboardPathForRole(role) {
  switch ((role || "").toLowerCase()) {
    case "admin":
      return "/dashboard/dashboard";
    case "manager":
      return "/DashboardManager/dashboard";
    case "user":
      return "/userprofile";
    case "superadmin":
      return "/DashboardSuper";
    default:
      return "/login";
  }
}

/** Reveals `[data-reveal]` blocks as they scroll into view.
 *
 *  Content is visible by default: the hiding styles only exist under
 *  `.nxl-reveal-on`, which this hook adds, so a visitor without JavaScript,
 *  without IntersectionObserver, or who prefers reduced motion still gets the
 *  whole page. A watchdog also reveals everything if the observer never
 *  delivers — an invisible page is far worse than a missing animation. */
function useScrollReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") return;

    const targets = Array.from(root.querySelectorAll("[data-reveal]"));
    if (!targets.length) return;

    root.classList.add("nxl-reveal-on");
    const revealAll = () => targets.forEach((el) => el.classList.add("is-in"));

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 },
    );
    targets.forEach((el) => io.observe(el));

    const watchdog = window.setTimeout(() => {
      if (!root.querySelector("[data-reveal].is-in")) revealAll();
    }, 2500);

    return () => {
      window.clearTimeout(watchdog);
      io.disconnect();
    };
  }, [rootRef]);
}

/** Desktop browser chrome around a product screenshot. */
function BrowserFrame({ src, alt, eager = false, className = "" }) {
  return (
    <figure className={`nxl-browser ${className}`}>
      <div className="nxl-browser-bar" aria-hidden="true">
        <span />
        <span />
        <span />
        <i className="nxl-browser-url" />
      </div>
      <img
        src={src}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : undefined}
      />
    </figure>
  );
}

/** Phone bezel around a real device screenshot. */
function PhoneFrame({ src, alt, eager = false, className = "" }) {
  return (
    <figure className={`nxl-phone ${className}`}>
      <div className="nxl-phone-screen">
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
        />
      </div>
    </figure>
  );
}

/** Two-segment EN/AR switch. Reuses the app-wide LanguageContext so a
 *  visitor who flips language on the landing page gets the same setting
 *  if they sign in — and vice versa. */
function LanguageSwitch({ language, toggleLanguage, label, className = "" }) {
  return (
    <div
      className={`nxl-lang-switch ${className}`}
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        className={language === "en" ? "is-active" : ""}
        aria-pressed={language === "en"}
        onClick={() => language !== "en" && toggleLanguage()}
      >
        EN
      </button>
      <button
        type="button"
        className={language === "ar" ? "is-active" : ""}
        aria-pressed={language === "ar"}
        onClick={() => language !== "ar" && toggleLanguage()}
      >
        AR
      </button>
    </div>
  );
}

/** Official WhatsApp glyph — lucide-react has no brand icons, and a plain
 *  phone/chat icon wouldn't read as "this opens WhatsApp" the way the mark does. */
function WhatsAppIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4c-4.33 0-7.85 3.48-7.85 7.78 0 1.37.36 2.71 1.05 3.89L4 20l4.44-1.16a7.9 7.9 0 0 0 3.61.9h.003c4.33 0 7.85-3.48 7.85-7.78 0-2.08-.82-4.03-2.3-5.5zm-5.55 11.97h-.003a6.6 6.6 0 0 1-3.35-.92l-.24-.14-2.5.65.67-2.43-.16-.25a6.46 6.46 0 0 1-1-3.42c0-3.58 2.94-6.5 6.56-6.5a6.53 6.53 0 0 1 4.63 1.9 6.4 6.4 0 0 1 1.92 4.6c0 3.58-2.94 6.5-6.56 6.5zm3.6-4.88c-.2-.1-1.16-.57-1.34-.63-.18-.07-.31-.1-.44.1-.13.2-.5.63-.62.76-.11.13-.23.14-.42.05-.2-.1-.83-.3-1.58-.96-.58-.51-.98-1.15-1.09-1.34-.11-.2-.01-.3.09-.4.09-.09.2-.23.3-.35.1-.11.13-.2.2-.33.07-.13.03-.25-.02-.35-.05-.1-.44-1.05-.6-1.44-.16-.38-.32-.33-.44-.33h-.38c-.13 0-.35.05-.53.25-.18.2-.7.68-.7 1.66s.72 1.93.82 2.06c.1.13 1.41 2.16 3.42 3.02.48.2.85.33 1.14.42.48.15.92.13 1.26.08.38-.06 1.16-.47 1.33-.93.16-.45.16-.85.11-.93-.05-.08-.18-.13-.38-.23z" />
    </svg>
  );
}

/** The floating "chat on WhatsApp" pill — modelled on the quiet, always-on
 *  bottom-corner widget most HR SaaS sites use (e.g. ZenHR), rather than a
 *  button competing for space in the header. Fixed to the viewport so it
 *  stays reachable no matter how far the visitor has scrolled. */
function WhatsAppFab({ label }) {
  return (
    <a
      className="nxl-whatsapp-fab"
      href={CONTACT_WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="nxl-whatsapp-fab-icon">
        <WhatsAppIcon size={25} />
      </span>
      <span className="nxl-whatsapp-fab-label">{label}</span>
    </a>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function LandingPage() {
  const rootRef = useRef(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { language, toggleLanguage } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  const shots = SHOTS[language] || SHOTS.en;
  const isRtl = t.dir === "rtl";
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;

  const TABS = useMemo(
    () => TAB_META.map((meta, i) => ({ ...meta, ...t.platform.tabs[i] })),
    [t],
  );
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [activeView, setActiveView] = useState(VIEW_META[0].id);
  useEffect(() => {
    // Keep the selected tab id valid if the language switch ever changes tab order.
    if (!TABS.some((tb) => tb.id === activeTab)) setActiveTab(TABS[0].id);
  }, [TABS, activeTab]);

  useScrollReveal(rootRef);

  const isAuthenticated = sessionManager.isAuthenticated();
  const appPath = isAuthenticated
    ? dashboardPathForRole(sessionManager.getUserRole())
    : "/login";
  const appLabel = isAuthenticated ? t.openDashboard : t.signIn;

  const openDemo = useCallback(() => {
    setMenuOpen(false);
    setDemoOpen(true);
  }, []);

  /* Mobile menu: lock scroll and close on Escape. */
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const tabIdx = Math.max(0, TABS.findIndex((tb) => tb.id === activeTab));
  const tab = TABS[tabIdx];

  /* Product tour autoplay: the active tab's progress bar is a CSS animation;
     when it ends we advance. It only runs while the tour is on screen and
     not hovered/focused, and stops for good once the visitor picks a tab. */
  const tourRef = useRef(null);
  const [tourAuto, setTourAuto] = useState(true);
  const [tourPaused, setTourPaused] = useState(false);
  const [tourInView, setTourInView] = useState(false);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setTourAuto(false);
      return undefined;
    }
    const el = tourRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setTourInView(entry.isIntersecting),
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const nextTab = () => setActiveTab(TABS[(tabIdx + 1) % TABS.length].id);
  const goTab = (id) => {
    setTourAuto(false);
    setActiveTab(id);
  };
  const stepTab = (dir) =>
    goTab(TABS[(tabIdx + dir + TABS.length) % TABS.length].id);

  /* Arrow keys move between tabs, mirrored for RTL as expected of a tablist. */
  const onTabKeyDown = (e) => {
    const nextKey = isRtl ? "ArrowLeft" : "ArrowRight";
    const prevKey = isRtl ? "ArrowRight" : "ArrowLeft";
    if (e.key !== nextKey && e.key !== prevKey) return;
    e.preventDefault();
    const i = TABS.findIndex((tb) => tb.id === activeTab);
    const next =
      e.key === nextKey
        ? (i + 1) % TABS.length
        : (i - 1 + TABS.length) % TABS.length;
    goTab(TABS[next].id);
    const el = document.getElementById(`nxl-tab-${TABS[next].id}`);
    if (el) el.focus();
  };

  return (
    <div className="nxl" ref={rootRef} dir={t.dir} lang={language}>
      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} />

      <a className="nxl-skip" href="#main">
        {t.skipToContent}
      </a>

      <WhatsAppFab />

      {/* ===================== Navigation ===================== */}
      <header className="nxl-nav" dir="ltr">
        <div className="nxl-container nxl-nav-inner">
          <a href="#left" className="nxl-brand" aria-label={t.brandHome}>
            <img src={nexployIcon} alt="Nexploy Icon" className="nxl-brand-icon" />
            <img src={logoDark} alt="Nexploy" width="180" height="44" className="nxl-brand-logo" />
          </a>

          <nav className="nxl-nav-links" aria-label="Primary">
            {NAV_KEYS.map((key, i) => (
              <a key={key} href={NAV_HREFS[i]}>
                {t.nav[key]}
              </a>
            ))}
          </nav>

          <div className="nxl-nav-cta">
            <LanguageSwitch
              language={language}
              toggleLanguage={toggleLanguage}
              label={t.langSwitchLabel}
            />
            <button
              type="button"
              className="nxl-btn nxl-btn-ghost"
              onClick={openDemo}
            >
              {t.requestDemo}
            </button>
            <Link className="nxl-btn nxl-btn-primary" to={appPath}>
              {appLabel}
            </Link>
          </div>

          <div className="nxl-nav-mobile-cluster">
            <LanguageSwitch
              language={language}
              toggleLanguage={toggleLanguage}
              label={t.langSwitchLabel}
              className="nxl-lang-switch-compact"
            />
            <button
              type="button"
              className="nxl-nav-toggle"
              aria-expanded={menuOpen}
              aria-controls="nxl-mobile-menu"
              aria-label={menuOpen ? t.menuClose : t.menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <div
          id="nxl-mobile-menu"
          className={`nxl-mobile-menu ${menuOpen ? "is-open" : ""}`}
          hidden={!menuOpen}
        >
          <nav aria-label="Mobile">
            {NAV_KEYS.map((key, i) => (
              <a
                key={key}
                href={NAV_HREFS[i]}
                onClick={() => setMenuOpen(false)}
              >
                {t.nav[key]}
              </a>
            ))}
          </nav>
          <div className="nxl-mobile-menu-cta">
            <Link
              className="nxl-btn nxl-btn-primary nxl-btn-block"
              to={appPath}
            >
              {appLabel}
            </Link>
            <button
              type="button"
              className="nxl-btn nxl-btn-outline nxl-btn-block"
              onClick={openDemo}
            >
              {t.requestDemo}
            </button>
          </div>
        </div>
      </header>

      <main id="main">
        {/* ===================== Hero ===================== */}
        <section className="nxl-hero" id="top">
          <div className="nxl-container nxl-hero-inner">
            <div className="nxl-hero-copy">
              <h1>
                <span>{t.hero.h1a}</span>
                <br />
                <span className="nxl-h1-accent">{t.hero.h1b}</span>
              </h1>
              <p className="nxl-hero-sub">{t.hero.sub}</p>

              <div className="nxl-hero-actions">
                <button
                  type="button"
                  className="nxl-btn nxl-btn-primary nxl-btn-lg"
                  onClick={openDemo}
                >
                  {t.requestDemo} <ArrowIcon size={18} aria-hidden="true" />
                </button>
                <Link
                  className="nxl-btn nxl-btn-outline nxl-btn-lg"
                  to={appPath}
                >
                  {appLabel}
                </Link>
              </div>

              <ul className="nxl-hero-points" style={{ marginTop: "30px" }}>
                {t.hero.points.map((text, i) => {
                  const Icon = TRUST_ICONS[i];
                  return (
                    <li key={text}>
                      <Icon size={15} aria-hidden="true" /> {text}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="nxl-hero-stage" >
              {/* The hero shot is the page's LCP image: load it eagerly and
                  at high priority rather than lazily. */}
              <img
                src={shots.shotDashboard}
                alt={t.hero.heroBrowserAlt}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="nxl-hero-browser"
              />
              <PhoneFrame
                src={shots.mobDashboard}
                alt={t.hero.heroPhoneAlt}
                eager
                className="nxl-hero-phone"
              />
            </div>
          </div>
        </section>

        {/* ===================== Product tour (showcase stage) ===================== */}
        <section className="nxl-section nxl-section-alt nxl-tour" id="platform">
          <div className="nxl-container">
            <div className="nxl-section-head" data-reveal>
              <span className="nxl-eyebrow">{t.platform.eyebrow}</span>
              <h2>{t.platform.h2}</h2>
              <p>{t.platform.p}</p>
            </div>

            <div
              className="nxl-tour-wrap"
              ref={tourRef}
              data-reveal
              onMouseEnter={() => setTourPaused(true)}
              onMouseLeave={() => setTourPaused(false)}
              onFocusCapture={() => setTourPaused(true)}
              onBlurCapture={() => setTourPaused(false)}
            >
              {/* Tab dock */}
              <div
                className="nxl-tour-dock"
                role="tablist"
                aria-label={t.platform.eyebrow}
                onKeyDown={onTabKeyDown}
              >
                {TABS.map((tb) => {
                  const Icon = tb.icon;
                  const on = activeTab === tb.id;
                  return (
                    <button
                      type="button"
                      key={tb.id}
                      id={`nxl-tab-${tb.id}`}
                      role="tab"
                      aria-selected={on}
                      aria-controls="nxl-tour-panel"
                      tabIndex={on ? 0 : -1}
                      className={`nxl-tour-tab ${on ? "is-active" : ""}`}
                      onClick={() => goTab(tb.id)}
                    >
                      <span className="nxl-tour-tab-icon" aria-hidden="true">
                        <Icon size={17} />
                      </span>
                      <span className="nxl-tour-tab-label">{tb.label}</span>
                      {on && (
                        <span
                          className={`nxl-tour-progress ${tourAuto ? "is-auto" : ""} ${
                            tourAuto && tourInView && !tourPaused ? "is-running" : ""
                          }`}
                          style={{ animationDuration: `${TOUR_MS}ms` }}
                          onAnimationEnd={nextTab}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Stage — the screenshot gets the full container width */}
              <div className="nxl-tour-stage">
                <span className="nxl-tour-glow" aria-hidden="true" />
                <figure className="nxl-browser nxl-tour-frame">
                  <div className="nxl-browser-bar" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <i className="nxl-browser-url" />
                  </div>
                  <div className="nxl-tour-screens">
                    {TABS.map((tb, i) => (
                      <img
                        key={tb.id}
                        src={shots[tb.img]}
                        alt={tb.id === tab.id ? tb.alt : ""}
                        aria-hidden={tb.id === tab.id ? undefined : "true"}
                        className={tb.id === tab.id ? "is-active" : ""}
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    ))}
                  </div>
                </figure>
              </div>

              {/* Caption */}
              <div
                className="nxl-tour-caption"
                id="nxl-tour-panel"
                role="tabpanel"
                aria-labelledby={`nxl-tab-${tab.id}`}
              >
                <div className="nxl-tour-caption-copy" key={tab.id}>
                  <div className="nxl-tour-meta">
                    <span className="nxl-tour-count">
                      {String(tabIdx + 1).padStart(2, "0")}
                      <small>/{String(TABS.length).padStart(2, "0")}</small>
                    </span>
                    <span className="nxl-pill nxl-pill-blue">{tab.tag}</span>
                  </div>
                  <h3>{tab.title}</h3>
                  <p>{tab.text}</p>
                </div>
                <ul className="nxl-tour-points" key={`${tab.id}-pts`}>
                  {tab.points.map((p) => (
                    <li key={p}>
                      <CheckCircle2 size={16} aria-hidden="true" /> {p}
                    </li>
                  ))}
                </ul>
                <div className="nxl-tour-arrows">
                  <button
                    type="button"
                    className="nxl-tour-arrow"
                    onClick={() => stepTab(-1)}
                    aria-label={language === "ar" ? "الشاشة السابقة" : "Previous screen"}
                  >
                    <BackIcon size={18} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="nxl-tour-arrow is-next"
                    onClick={() => stepTab(1)}
                    aria-label={language === "ar" ? "الشاشة التالية" : "Next screen"}
                  >
                    <ArrowIcon size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* ===================== Attendance ===================== */}
        <section className="nxl-section" id="attendance">
          <div className="nxl-container">
            <div className="nxl-split" data-reveal>
              <div className="nxl-split-copy">
                <span className="nxl-pill nxl-pill-blue">
                  {t.attendance.pill}
                </span>
                <h2>{t.attendance.h2}</h2>
                <p>{t.attendance.p}</p>
                <ul className="nxl-feature-list">
                  {[
                    Fingerprint,
                    Timer,
                    CheckCircle2,
                    MapPin,
                    FileSpreadsheet,
                  ].map((Icon, i) => (
                    <li key={i}>
                      <Icon size={17} aria-hidden="true" />{" "}
                      {t.attendance.points[i]}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="nxl-split-media">
                <BrowserFrame src={shots.shotAttendance} alt={t.attendance.pill} />
                <PhoneFrame
                  src={shots.mobCheckin}
                  alt={t.attendance.splitPhoneAlt}
                  className="nxl-split-phone"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===================== Payroll ===================== */}
        <section className="nxl-section nxl-section-dark" id="payroll">
          <div className="nxl-container">
            <div className="nxl-split nxl-split-reverse" data-reveal>
              <div className="nxl-split-copy">
                <span className="nxl-pill nxl-pill-onDark">
                  {t.payroll.pill}
                </span>
                <h2>{t.payroll.h2}</h2>
                <p>{t.payroll.p}</p>
                <ul className="nxl-feature-list nxl-feature-list-onDark">
                  {[
                    Banknote,
                    TrendingUp,
                    Calculator,
                    Repeat,
                    FileSpreadsheet,
                  ].map((Icon, i) => (
                    <li key={i}>
                      <Icon size={17} aria-hidden="true" />{" "}
                      {t.payroll.points[i]}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="nxl-split-media">
                <figure className="nxl-photo">
                  <img
                    src={shots.shotPayroll}
                    alt={t.payroll.photoAlt}
                    loading="lazy"
                    decoding="async"
                  />
                </figure>
                <PhoneFrame
                  src={shots.mobSalary}
                  alt={t.payroll.splitPhoneAlt}
                  className="nxl-split-phone"
                />
              </div>
            </div>
          </div>
        </section>


        {/* ===================== Web + Mobile ===================== */}
        <section className="nxl-section" id="mobile">
          <div className="nxl-container">
            <div className="nxl-section-head" data-reveal>
              <span className="nxl-eyebrow">{t.mobile.eyebrow}</span>
              <h2>{t.mobile.h2}</h2>
              <p>{t.mobile.p}</p>
            </div>

            <div className="nxl-duo" data-reveal>
              <article className="nxl-duo-card">
                <div className="nxl-duo-icon">
                  <Monitor size={22} aria-hidden="true" />
                </div>
                <h3>{t.mobile.web.h3}</h3>
                <p>{t.mobile.web.p}</p>
                <ul className="nxl-check-list">
                  {t.mobile.web.points.map((p) => (
                    <li key={p}>
                      <CheckCircle2 size={16} aria-hidden="true" /> {p}
                    </li>
                  ))}
                </ul>
              </article>

              <article className="nxl-duo-card">
                <div className="nxl-duo-icon">
                  <Smartphone size={22} aria-hidden="true" />
                </div>
                <h3>{t.mobile.app.h3}</h3>
                <p>{t.mobile.app.p}</p>
                <ul className="nxl-check-list">
                  {t.mobile.app.points.map((p) => (
                    <li key={p}>
                      <CheckCircle2 size={16} aria-hidden="true" /> {p}
                    </li>
                  ))}
                </ul>
              </article>
            </div>

            <div className="nxl-phone-rail" data-reveal>
              {t.mobile.screens.map((s, i) => (
                <div className="nxl-phone-rail-item" key={s.label}>
                  <PhoneFrame src={shots[MOBILE_SCREEN_IMGS[i]]} alt={s.alt} />
                  <span className="nxl-phone-caption">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== Employee self-service ===================== */}
        <section className="nxl-section nxl-section-alt" id="self-service">
          <div className="nxl-container">
            <div className="nxl-section-head" data-reveal>
              <span className="nxl-eyebrow">{t.selfService.eyebrow}</span>
              <h2>{t.selfService.h2}</h2>
              <p>{t.selfService.p}</p>
            </div>

            <ul className="nxl-mini-grid" data-reveal>
                {t.selfService.items.map(({ title, text }, i) => {
                  const Icon = SELF_SERVICE_ICONS[i];
                  return (
                    <li key={title}>
                      <span className="nxl-mini-icon">
                        <Icon size={18} aria-hidden="true" />
                      </span>
                      <div>
                        <h3>{title}</h3>
                        <p>{text}</p>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </section>

        {/* ===================== Automation / workflows ===================== */}
        <section className="nxl-section" id="automation">
          <div className="nxl-container">
            <div className="nxl-section-head" data-reveal>
              <span className="nxl-eyebrow">{t.automation.eyebrow}</span>
              <h2>{t.automation.h2}</h2>
              <p>{t.automation.p}</p>
            </div>

            <ol className="nxl-flow" data-reveal>
              {t.automation.steps.map(({ title, text }, i) => {
                const Icon = WORKFLOW_ICONS[i];
                return (
                  <li key={title}>
                    <span className="nxl-flow-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="nxl-flow-icon">
                      <Icon size={20} aria-hidden="true" />
                    </span>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </li>
                );
              })}
            </ol>

            <div className="nxl-split nxl-split-tight" data-reveal>
              <div className="nxl-split-copy">
                <span className="nxl-pill nxl-pill-blue">
                  {t.automation.escalation.pill}
                </span>
                <h3 className="nxl-h3-lg">{t.automation.escalation.h3}</h3>
                <p>{t.automation.escalation.p}</p>
                <ul className="nxl-feature-list">
                  {[Layers, Eye, Mic].map((Icon, i) => (
                    <li key={i}>
                      <Icon size={17} aria-hidden="true" />{" "}
                      {t.automation.escalation.points[i]}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="nxl-split-media">
                <BrowserFrame
                  src={shots.shotEscalations}
                  alt={t.automation.escalation.alt}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===================== Admin control & access ===================== */}
        {/* ===================== One account, three views ===================== */}
        <section className="nxl-section nxl-section-alt nxl-views" id="views">
          <div className="nxl-container">
            <div className="nxl-section-head" data-reveal>
              <span className="nxl-eyebrow">{t.views.eyebrow}</span>
              <h2>{t.views.h2}</h2>
              <p>{t.views.p}</p>
            </div>

            <div className="nxl-views-wrap" data-reveal>
              {/* Replica of the in-app view switcher */}
              <div className="nxl-views-switch-row">
                <div
                  className="nxl-views-switch"
                  role="tablist"
                  aria-label={t.views.eyebrow}
                >
                  {VIEW_META.map((v, i) => {
                    const Icon = v.icon;
                    const on = activeView === v.id;
                    return (
                      <button
                        type="button"
                        key={v.id}
                        role="tab"
                        id={`nxl-view-${v.id}`}
                        aria-selected={on}
                        aria-controls="nxl-views-panel"
                        className={`nxl-views-seg ${on ? "is-active" : ""}`}
                        onClick={() => setActiveView(v.id)}
                      >
                        <Icon size={16} aria-hidden="true" />
                        <span>{t.views.items[i].label}</span>
                      </button>
                    );
                  })}
                </div>
                <span className="nxl-views-hint">{t.views.hint}</span>
              </div>

              <figure className="nxl-browser nxl-views-frame">
                <div className="nxl-browser-bar" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <i className="nxl-browser-url" />
                </div>
                <div className="nxl-tour-screens">
                  {VIEW_META.map((v, i) => (
                    <img
                      key={v.id}
                      src={shots[v.img]}
                      alt={activeView === v.id ? t.views.items[i].alt : ""}
                      aria-hidden={activeView === v.id ? undefined : "true"}
                      className={activeView === v.id ? "is-active" : ""}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
              </figure>

              {/* One card per view — also clickable */}
              <div className="nxl-views-cards" id="nxl-views-panel" role="tabpanel" aria-labelledby={`nxl-view-${activeView}`}>
                {VIEW_META.map((v, i) => {
                  const Icon = v.icon;
                  const item = t.views.items[i];
                  const on = activeView === v.id;
                  return (
                    <button
                      type="button"
                      key={v.id}
                      className={`nxl-views-card ${on ? "is-active" : ""}`}
                      onClick={() => setActiveView(v.id)}
                      aria-pressed={on}
                    >
                      <span className="nxl-views-card-icon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span className="nxl-views-card-body">
                        <span className="nxl-views-card-label">{item.label}</span>
                        <strong>{item.title}</strong>
                        <span className="nxl-views-card-text">{item.text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ===================== Admin levels ===================== */}
        <section className="nxl-section nxl-section-dark nxl-levels" id="control">
          <div className="nxl-container">
            <div
              className="nxl-section-head nxl-section-head-onDark"
              data-reveal
            >
              <span className="nxl-eyebrow nxl-eyebrow-onDark">
                {t.control.eyebrow}
              </span>
              <h2>{t.control.h2}</h2>
              <p>{t.control.p}</p>
            </div>

            <div className="nxl-levels-shot" data-reveal>
              <span className="nxl-tour-glow" aria-hidden="true" />
              <BrowserFrame src={shots.adminLevels} alt={t.control.alt} />
            </div>

            <ol className="nxl-levels-steps" data-reveal>
              {t.control.steps.map(({ title, text }, i) => {
                const Icon = LEVEL_STEP_ICONS[i];
                return (
                  <li className="nxl-levels-step" key={title}>
                    <div className="nxl-levels-step-top">
                      <span className="nxl-levels-step-num">{i + 1}</span>
                      <Icon size={18} aria-hidden="true" />
                    </div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                    {i === 1 && (
                      <div className="nxl-levels-states">
                        {PAGE_STATE_META.map((st, k) => {
                          const SIcon = st.icon;
                          return (
                            <span className={`nxl-levels-state is-${st.id}`} key={st.id}>
                              <SIcon size={13} aria-hidden="true" />
                              {t.control.states[k]}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>

            <ul className="nxl-levels-extras" data-reveal>
              {t.control.extras.map((text, i) => {
                const Icon = CONTROL_EXTRA_ICONS[i];
                return (
                  <li key={text}>
                    <Icon size={15} aria-hidden="true" />
                    {text}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* The module grid is hidden for now. Its copy is still in
            translations.js under `modules` (plus `nav.modules` and the
            footer's "All Modules" label) if it needs to come back. */}

        {/* ===================== Final CTA ===================== */}
        <section className="nxl-cta" id="contact">
          <div className="nxl-container nxl-cta-inner">
            <h2>{t.cta.h2}</h2>
            <p>{t.cta.p}</p>
            <div className="nxl-hero-actions nxl-center">
              <button
                type="button"
                className="nxl-btn nxl-btn-light nxl-btn-lg"
                onClick={openDemo}
              >
                <Mail size={18} aria-hidden="true" /> {t.requestDemo}
              </button>
              <a
                className="nxl-btn nxl-btn-outline-light nxl-btn-lg"
                href={CONTACT_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsAppIcon size={18} /> {t.callUs}
              </a>
              <Link
                className="nxl-btn nxl-btn-outline-light nxl-btn-lg"
                to={appPath}
              >
                {appLabel}
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ===================== Footer ===================== */}
      <footer className="nxl-footer">
        <div className="nxl-container">
          <div className="nxl-footer-top">
            <div className="nxl-footer-brand">
              <img src={logoWhite} alt="Nexploy" width="150" height="32" />
              <p>{t.footer.brandText}</p>
            </div>

            <nav className="nxl-footer-nav" aria-label="Footer">
              <div>
                <h3>{t.footer.columns.platform.title}</h3>
                <a href="#platform">{t.footer.columns.platform.links[0]}</a>
                <a href="#attendance">{t.footer.columns.platform.links[1]}</a>
                <a href="#payroll">{t.footer.columns.platform.links[2]}</a>
              </div>
              <div>
                <h3>{t.footer.columns.people.title}</h3>
                <a href="#self-service">{t.footer.columns.people.links[0]}</a>
                <a href="#mobile">{t.footer.columns.people.links[1]}</a>
                <a href="#automation">{t.footer.columns.people.links[2]}</a>
                <a href="#control">{t.footer.columns.people.links[3]}</a>
              </div>
              <div>
                <h3>{t.footer.columns.start.title}</h3>
                <Link to={appPath}>{appLabel}</Link>
                <a href="#contact">{t.footer.columns.start.demoLink}</a>
                <a href={CONTACT_PHONE_TEL}>
                  <bdi>{t.phoneDisplay}</bdi>
                </a>
                <a href="mailto:support@nexploy-eg.com">
                  support@nexploy-eg.com
                </a>
                <a href="https://nexploy-eg.com" rel="noreferrer">
                  www.nexploy-eg.com
                </a>
              </div>
            </nav>
          </div>

          <div className="nxl-footer-bottom">
            <p>{t.footer.copyright(new Date().getFullYear())}</p>
            <div className="nxl-footer-contact">
              <a href={CONTACT_PHONE_TEL}>
                <Phone size={15} aria-hidden="true" />{" "}
                <bdi>{t.phoneDisplay}</bdi>
              </a>
              <a href="mailto:support@nexploy-eg.com">
                <Mail size={15} aria-hidden="true" /> support@nexploy-eg.com
              </a>
              <a href="https://nexploy-eg.com" rel="noreferrer">
                <Globe size={15} aria-hidden="true" /> nexploy-eg.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
